import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Dimensionet në mm
const COVER_WIDTH_MM = 330; // 33cm
const COVER_HEIGHT_MM = 480; // 48cm
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

// DPI për cilësi të lartë printimi
const DPI = 300;

// Konverto mm në pixels
const mmToPixels = (mm, dpi = DPI) => Math.round((mm / 25.4) * dpi);

/**
 * Gjeneron PDF të kombinuar me të gjitha faqet dhe info të klientit
 * @param {Array<HTMLElement>} pageElements - Array me elementet e faqeve
 * @param {Object} customerInfo - Info e klientit (emri, mbiemri, etj)
 * @param {Object} productInfo - Info e produktit
 * @param {Function} onProgress - Callback për progress
 * @returns {Promise<Blob>} - PDF blob
 */
export const generateOrderPDF = async (pageElements, customerInfo, productInfo, onProgress = () => {}) => {
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const totalSteps = pageElements.length + 1; // +1 for cover page with info
    let currentStep = 0;

    // Shto faqen e parë me info të klientit
    onProgress({ step: ++currentStep, total: totalSteps, message: 'Duke krijuar faqen e informacionit...' });
    
    // Header
    pdf.setFillColor(102, 126, 234);
    pdf.rect(0, 0, A4_WIDTH_MM, 40, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('POROSI - PHOTO BOOK', A4_WIDTH_MM / 2, 25, { align: 'center' });

    // Customer Info
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Te dhenat e Klientit:', 20, 55);
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    let yPos = 70;
    pdf.text(`Emri: ${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`, 20, yPos);
    yPos += 10;
    pdf.text(`Vendi: ${customerInfo.city || ''}`, 20, yPos);
    yPos += 10;
    pdf.text(`Adresa: ${customerInfo.address || ''}`, 20, yPos);
    yPos += 10;
    pdf.text(`Telefoni: ${customerInfo.phone || ''}`, 20, yPos);
    
    if (customerInfo.comments) {
      yPos += 10;
      pdf.text(`Komenti: ${customerInfo.comments}`, 20, yPos);
    }

    // Product Info
    yPos += 25;
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Detajet e Produktit:', 20, yPos);
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    yPos += 15;
    pdf.text(`Produkti: ${productInfo.name || 'Photo Book'}`, 20, yPos);
    yPos += 10;
    pdf.text(`Cmimi: ${productInfo.price || 0} EUR`, 20, yPos);
    yPos += 10;
    pdf.text(`Numri i faqeve: ${pageElements.length}`, 20, yPos);
    yPos += 10;
    pdf.text(`Data: ${new Date().toLocaleDateString('sq-AL')}`, 20, yPos);

    // Divider
    yPos += 20;
    pdf.setDrawColor(102, 126, 234);
    pdf.setLineWidth(0.5);
    pdf.line(20, yPos, A4_WIDTH_MM - 20, yPos);
    
    yPos += 15;
    pdf.setFontSize(11);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Faqet e Photo Book vijne me poshte...', A4_WIDTH_MM / 2, yPos, { align: 'center' });

    // Shto secilën faqe
    for (let i = 0; i < pageElements.length; i++) {
      onProgress({ 
        step: ++currentStep, 
        total: totalSteps, 
        message: `Duke eksportuar faqen ${i + 1}/${pageElements.length}...` 
      });

      if (pageElements[i]) {
        // Shto faqe të re për çdo page
        pdf.addPage('a4', 'portrait');

        // Krijo canvas nga elementi
        const canvas = await html2canvas(pageElements[i], {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        // Shto imazhin në PDF
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM);

        // Shto numrin e faqes
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Faqja ${i + 1}`, A4_WIDTH_MM - 20, A4_HEIGHT_MM - 10, { align: 'right' });
      }
    }

    // Kthe PDF si blob
    const pdfBlob = pdf.output('blob');
    
    return {
      blob: pdfBlob,
      filename: `PhotoBook_Order_${Date.now()}.pdf`,
      success: true
    };
  } catch (error) {
    console.error('Error generating order PDF:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eksporton një element HTML si PDF me dimensione të specifikuara
 * @param {HTMLElement} element - Elementi për eksport
 * @param {string} filename - Emri i file-it
 * @param {number} widthMM - Gjerësia në mm
 * @param {number} heightMM - Lartësia në mm
 * @returns {Promise<Blob>} - PDF blob
 */
export const exportElementToPDF = async (element, filename, widthMM, heightMM) => {
  try {
    // Krijo canvas nga elementi
    const canvas = await html2canvas(element, {
      scale: 2, // Shkallëzim për cilësi më të mirë
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false
    });

    // Krijo PDF me dimensionet e specifikuara
    const pdf = new jsPDF({
      orientation: widthMM > heightMM ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [widthMM, heightMM]
    });

    // Shto imazhin në PDF
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(imgData, 'JPEG', 0, 0, widthMM, heightMM);

    // Ruaj PDF
    const pdfBlob = pdf.output('blob');
    
    return {
      blob: pdfBlob,
      filename: `${filename}.pdf`,
      success: true
    };
  } catch (error) {
    console.error('Error exporting PDF:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eksporton Cover (33x48cm)
 * @param {HTMLElement} coverElement - Elementi i cover-it
 * @param {string} type - 'front' ose 'back'
 * @returns {Promise<Object>}
 */
export const exportCoverToPDF = async (coverElement, type = 'front') => {
  return exportElementToPDF(
    coverElement,
    `Cover_${type}`,
    COVER_WIDTH_MM,
    COVER_HEIGHT_MM
  );
};

/**
 * Eksporton një faqe (A4)
 * @param {HTMLElement} pageElement - Elementi i faqes
 * @param {number} pageNumber - Numri i faqes
 * @returns {Promise<Object>}
 */
export const exportPageToPDF = async (pageElement, pageNumber) => {
  return exportElementToPDF(
    pageElement,
    `Page_${String(pageNumber).padStart(2, '0')}`,
    A4_WIDTH_MM,
    A4_HEIGHT_MM
  );
};

/**
 * Eksporton të gjithë librin (Cover + Faqet)
 * @param {Object} bookData - Të dhënat e librit
 * @param {HTMLElement} coverFrontRef - Ref për cover front
 * @param {HTMLElement} coverBackRef - Ref për cover back
 * @param {Array<HTMLElement>} pageRefs - Array me refs për faqet
 * @param {Function} onProgress - Callback për progress
 * @returns {Promise<Object>}
 */
export const exportFullBookToPDF = async (
  coverFrontRef,
  coverBackRef,
  pageRefs,
  onProgress = () => {}
) => {
  const results = {
    coverFront: null,
    coverBack: null,
    pages: [],
    success: true,
    errors: []
  };

  const totalSteps = 2 + pageRefs.length; // 2 covers + pages
  let currentStep = 0;

  try {
    // Eksporto Cover Front
    onProgress({ step: ++currentStep, total: totalSteps, message: 'Duke eksportuar Cover Front...' });
    if (coverFrontRef) {
      results.coverFront = await exportCoverToPDF(coverFrontRef, 'Front');
      if (!results.coverFront.success) {
        results.errors.push('Cover Front failed');
      }
    }

    // Eksporto Cover Back
    onProgress({ step: ++currentStep, total: totalSteps, message: 'Duke eksportuar Cover Back...' });
    if (coverBackRef) {
      results.coverBack = await exportCoverToPDF(coverBackRef, 'Back');
      if (!results.coverBack.success) {
        results.errors.push('Cover Back failed');
      }
    }

    // Eksporto të gjitha faqet
    for (let i = 0; i < pageRefs.length; i++) {
      onProgress({ 
        step: ++currentStep, 
        total: totalSteps, 
        message: `Duke eksportuar Faqen ${i + 1}...` 
      });
      
      if (pageRefs[i]) {
        const pageResult = await exportPageToPDF(pageRefs[i], i + 1);
        results.pages.push(pageResult);
        if (!pageResult.success) {
          results.errors.push(`Page ${i + 1} failed`);
        }
      }
    }

    results.success = results.errors.length === 0;
    return results;
  } catch (error) {
    console.error('Error exporting full book:', error);
    return {
      ...results,
      success: false,
      errors: [...results.errors, error.message]
    };
  }
};

/**
 * Krijon ZIP file me të gjitha PDF-të
 * @param {Object} exportResults - Rezultatet nga exportFullBookToPDF
 * @returns {Promise<Blob>}
 */
export const createPDFZipBundle = async (exportResults) => {
  // Për ZIP do përdorim JSZip (mund ta shtojmë më vonë)
  // Për tani, kthejmë rezultatet si array
  const files = [];
  
  if (exportResults.coverFront?.blob) {
    files.push({
      name: exportResults.coverFront.filename,
      blob: exportResults.coverFront.blob
    });
  }
  
  if (exportResults.coverBack?.blob) {
    files.push({
      name: exportResults.coverBack.filename,
      blob: exportResults.coverBack.blob
    });
  }
  
  exportResults.pages.forEach(page => {
    if (page?.blob) {
      files.push({
        name: page.filename,
        blob: page.blob
      });
    }
  });
  
  return files;
};

/**
 * Shkarko një PDF të vetëm
 * @param {Blob} blob - PDF blob
 * @param {string} filename - Emri i file-it
 */
export const downloadPDF = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Shkarko të gjitha PDF-të si files individuale
 * @param {Array} files - Array me {name, blob}
 */
export const downloadAllPDFs = (files) => {
  files.forEach((file, index) => {
    setTimeout(() => {
      downloadPDF(file.blob, file.name);
    }, index * 500); // Delay për të shmangur blocking
  });
};

export default {
  exportCoverToPDF,
  exportPageToPDF,
  exportFullBookToPDF,
  createPDFZipBundle,
  downloadPDF,
  downloadAllPDFs,
  generateOrderPDF,
  COVER_WIDTH_MM,
  COVER_HEIGHT_MM,
  A4_WIDTH_MM,
  A4_HEIGHT_MM
};
